import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { toast } from "sonner";
import {
  Loader2, AlertTriangle, ChevronRight, Eye, Upload, FileText,
  CheckCircle2, RefreshCw, X,
} from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";

const CONFIRM_PHRASE = "IMPORT";
const ACCEPTED_TYPES = ".csv,text/csv,application/vnd.ms-excel";
const MAX_BYTES = 25 * 1024 * 1024;

function Stat({ label, value, tone }) {
  const toneClass =
    tone === "good"   ? "text-emerald-600" :
    tone === "warn"   ? "text-amber-600" :
    tone === "bad"    ? "text-rose-600"   :
    tone === "muted"  ? "text-ink/50"     : "text-ink";
  return (
    <div className="flex flex-col gap-0.5 min-w-[88px]">
      <div className="data-sku text-[10px]">{label}</div>
      <div className={`font-display font-black text-2xl leading-none ${toneClass}`}>{value}</div>
    </div>
  );
}

function StepHeader({ index, title, status }) {
  const tone =
    status === "done"    ? "bg-emerald-600 text-white"
  : status === "active"  ? "bg-ink text-white"
  : "bg-secondary text-ink/50";
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center font-display font-black text-sm ${tone}`}>
        {status === "done" ? <CheckCircle2 size={16} /> : index}
      </div>
      <h2 className="font-display font-black text-lg sm:text-xl tracking-tight">{title}</h2>
    </div>
  );
}

export default function CatalogImportCsvPage() {
  const [depts, setDepts] = useState([]);
  const [selectedDepts, setSelectedDepts] = useState([]); // empty = no filter / all

  const [file, setFile] = useState(null);
  const [limit, setLimit] = useState("");

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const [confirmText, setConfirmText] = useState("");

  // Load departments
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/departments");
        setDepts(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e));
      }
    })();
  }, []);

  const limitInt = useMemo(() => {
    if (limit === "" || limit === null) return null;
    const n = parseInt(limit, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [limit]);
  const limitInvalid = limit !== "" && limitInt === null;

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`File too large (max ${MAX_BYTES / (1024 * 1024)} MB)`);
      return;
    }
    setFile(f);
    setPreview(null);
    setResult(null);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setConfirmText("");
  };

  const toggleDept = (name) => {
    setSelectedDepts((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
    setPreview(null);
    setResult(null);
  };

  const runDryRun = async () => {
    if (!file) { toast.error("Pick a CSV file first"); return; }
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", "true");
      if (selectedDepts.length > 0) form.append("departments", JSON.stringify(selectedDepts));
      if (limitInt !== null) form.append("limit", String(limitInt));
      const { data } = await api.post("/admin/catalog/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Preview ready — would import ${data.would_import_count} row${data.would_import_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPreviewing(false);
    }
  };

  const commitImport = async () => {
    if (!file) return;
    if (confirmText.trim() !== CONFIRM_PHRASE) {
      toast.error(`Type ${CONFIRM_PHRASE} to confirm`);
      return;
    }
    setCommitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", "false");
      if (selectedDepts.length > 0) form.append("departments", JSON.stringify(selectedDepts));
      if (limitInt !== null) form.append("limit", String(limitInt));
      const { data } = await api.post("/admin/catalog/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setConfirmText("");
      toast.success(`Imported ${data.imported_count} product${data.imported_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setCommitting(false);
    }
  };

  const step1Status = file ? "done" : "active";
  const step2Status = !file ? "idle" : previewing ? "active" : preview ? "done" : "active";
  const step3Status = !preview ? "idle" : committing ? "active" : result ? "done" : "active";

  const previewCounts = preview ? [
    { label: "CSV rows",         value: preview.total_csv_rows,                tone: "muted" },
    { label: "Eligible",         value: preview.eligible_rows,                 tone: "good"  },
    { label: "Would import",     value: preview.would_import_count,            tone: "good"  },
    { label: "Errors",           value: preview.skipped_error_count,           tone: preview.skipped_error_count ? "bad" : "muted" },
    { label: "Inactive",         value: preview.skipped_inactive_count,        tone: "muted" },
    { label: "Other depts",      value: preview.skipped_department_filter_count, tone: "muted" },
    { label: "Already in Mongo", value: preview.skipped_existing_mongo_count,  tone: preview.skipped_existing_mongo_count ? "warn" : "muted" },
    { label: "Mapping ignored",  value: preview.skipped_mapping_ignored_count, tone: "muted" },
  ] : [];

  return (
    <div className="max-w-4xl" data-testid="admin-catalog-import-csv-page">
      <PageHeader
        eyebrow="Catalog management"
        title="CSV Catalog Import"
        subtitle={
          <>
            Upload a CSV file as an alternative to the Google Sheets workflow. Rows are inserted into the
            live MongoDB catalog. Idempotent — barcodes that already exist are always skipped (upsert-safe).
            Required columns: <span className="font-mono">barcode</span>, <span className="font-mono">product_name</span>,{" "}
            <span className="font-mono">department_id</span>, <span className="font-mono">active</span>.
          </>
        }
      />

      {/* STEP 1 — UPLOAD */}
      <Card variant="raised" className="p-5 mb-4" data-testid="csv-step-upload">
        <StepHeader index={1} title="Upload CSV" status={step1Status} />
        <p className="text-sm text-ink/60 mb-4">
          UTF-8 encoded, comma-separated, header row required. Max {MAX_BYTES / (1024 * 1024)} MB.
        </p>

        {!file && (
          <label
            className="block border-2 border-dashed border-border/60 rounded-card p-6 text-center cursor-pointer hover:border-brand/40 hover:bg-brand-tint/30 transition-all"
            data-testid="csv-file-dropzone"
          >
            <input
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={onFileChange}
              className="hidden"
              data-testid="csv-file-input"
            />
            <FileText size={28} className="mx-auto text-ink-3 mb-2" />
            <div className="font-display font-semibold text-sm">Click to choose a CSV file</div>
            <div className="data-sku mt-1">or drag &amp; drop a .csv file here</div>
          </label>
        )}

        {file && (
          <div className="flex items-center gap-3 rounded-button p-3 bg-paper-sunk shadow-rest" data-testid="csv-file-chip">
            <FileText size={18} className="shrink-0 text-ink-2" />
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold text-sm truncate" data-testid="csv-file-name">{file.name}</div>
              <div className="data-sku">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
            <button
              onClick={clearFile}
              className="h-8 w-8 flex items-center justify-center rounded-chip text-ink-3 hover:bg-status-urgent-bg hover:text-status-urgent transition-colors"
              aria-label="Remove file"
              data-testid="csv-file-clear"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </Card>

      {/* STEP 2 — PREVIEW */}
      <Card
        variant="raised"
        className={`p-5 mb-4 transition-opacity ${!file ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="csv-step-preview"
      >
        <StepHeader index={2} title="Preview import" status={step2Status} />
        <p className="text-sm text-ink/60 mb-4">
          Dry-run with optional department filter. Writes nothing.
        </p>

        <div className="mb-4">
          <div className="data-sku mb-2">Departments filter <span className="text-ink/40">(optional — leave empty for all)</span></div>
          <div className="flex flex-wrap gap-2" data-testid="csv-department-chips">
            {depts.length === 0 ? (
              <div className="text-xs text-ink/50">No departments configured</div>
            ) : depts.map((d) => {
              const active = selectedDepts.includes(d.name);
              return (
                <button
                  key={d.id}
                  onClick={() => toggleDept(d.name)}
                  className={`h-9 px-3 rounded-chip text-sm font-display tracking-wide transition-all duration-150 ease-out active:scale-[0.98] ${
                    active
                      ? "bg-ink text-white shadow-rest"
                      : "bg-paper-2 text-ink/70 shadow-rest hover:shadow-raised"
                  }`}
                  data-testid={`csv-dept-chip-${d.name.toLowerCase()}`}
                >
                  {deptLabel(d.name)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 max-w-xs">
          <div className="data-sku mb-1">Limit (optional)</div>
          <input
            type="number"
            min="0"
            value={limit}
            onChange={(e) => { setLimit(e.target.value); setPreview(null); setResult(null); }}
            placeholder="e.g. 50"
            className="w-full h-11 px-3 rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none transition-shadow"
            data-testid="csv-limit-input"
          />
          {limitInvalid && (
            <div className="text-xs text-rose-600 mt-1">Limit must be a non-negative integer</div>
          )}
        </div>

        <Button
          tier="secondary"
          onClick={runDryRun}
          disabled={previewing || !file || limitInvalid}
          data-testid="csv-preview-button"
        >
          {previewing ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}
          {previewing ? "Previewing…" : preview ? "Re-run preview" : "Preview (dry-run)"}
        </Button>

        {preview && (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="csv-preview-summary">
              {previewCounts.map((c) => <Stat key={c.label} {...c} />)}
            </div>

            {preview.detected_headers?.length > 0 && (
              <div className="mt-4 data-sku" data-testid="csv-detected-headers">
                Detected columns: <span className="font-mono text-ink/70">{preview.detected_headers.join(", ")}</span>
              </div>
            )}

            {preview.limited_by === "limit" && (
              <div className="mt-3 text-xs text-amber-700 flex items-center gap-1.5">
                <AlertTriangle size={12} />
                Capped by limit. {preview.eligible_rows - preview.would_import_count} more eligible rows are waiting beyond the cap.
              </div>
            )}

            {preview.sample_imports?.length > 0 && (
              <div className="mt-4">
                <div className="data-sku mb-2">Sample of rows to import</div>
                <div className="border border-border rounded-sm overflow-hidden">
                  {preview.sample_imports.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2 text-sm border-b border-border last:border-0"
                      data-testid={`csv-preview-sample-${i}`}
                    >
                      <div className="font-mono text-xs text-ink/50 w-12">#{s.row}</div>
                      <div className="font-mono text-xs text-ink/70 w-28 truncate">{s.barcode}</div>
                      <div className="flex-1 font-display font-semibold truncate">{s.name}</div>
                      <div className="data-sku text-[10px]">{s.department}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.sample_skips?.length > 0 && (
              <div className="mt-4">
                <div className="data-sku mb-2 text-rose-700">Sample skipped (errors / inactive)</div>
                <div className="border border-rose-200 bg-rose-50/40 rounded-sm overflow-hidden">
                  {preview.sample_skips.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2 text-xs border-b border-rose-100 last:border-0"
                      data-testid={`csv-preview-skip-${i}`}
                    >
                      <div className="font-mono text-ink/50 w-12">#{s.row}</div>
                      <div className="font-mono text-ink/70 w-28 truncate">{s.barcode || "—"}</div>
                      <div className="flex-1 text-ink/80">{s.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* STEP 3 — COMMIT */}
      <Card
        variant="raised"
        className={`p-5 mb-10 transition-opacity ${!preview ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="csv-step-commit"
      >
        <StepHeader index={3} title="Confirm & import" status={step3Status} />

        {preview && (
          <p className="text-sm text-ink/70 mb-4">
            Will insert <span className="font-display font-bold text-ink">{preview.would_import_count}</span> product
            {preview.would_import_count === 1 ? "" : "s"} into MongoDB
            {selectedDepts.length > 0 && (
              <>
                {" "}(department{selectedDepts.length === 1 ? "" : "s"}:&nbsp;
                <span className="font-display font-semibold text-ink">{selectedDepts.join(", ")}</span>)
              </>
            )}.
            This action is logged to Action History.
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-xl">
          <div className="flex-1">
            <div className="data-sku mb-1">Type <span className="font-mono text-ink">{CONFIRM_PHRASE}</span> to confirm</div>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="w-full h-11 px-3 rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none transition-shadow font-mono tracking-widest uppercase"
              data-testid="csv-confirm-input"
              disabled={!preview || committing}
            />
          </div>
          <Button
            tier="primary"
            onClick={commitImport}
            disabled={!preview || committing || confirmText.trim() !== CONFIRM_PHRASE || (preview && preview.would_import_count === 0)}
            className="sm:w-auto w-full"
            data-testid="csv-commit-button"
          >
            {committing ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            {committing ? "Importing…" : "Import now"}
          </Button>
        </div>

        {result && (
          <div className="mt-5" data-testid="csv-import-result">
            <div className="flex items-center gap-2 mb-3 text-emerald-700 font-display font-bold">
              <CheckCircle2 size={18} /> Import complete
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Imported"          value={result.imported_count} tone="good" />
              <Stat label="Eligible"          value={result.eligible_rows} tone="muted" />
              <Stat label="Errors"            value={result.skipped_error_count} tone={result.skipped_error_count ? "bad" : "muted"} />
              <Stat label="Inactive"          value={result.skipped_inactive_count} tone="muted" />
              <Stat label="Other depts"       value={result.skipped_department_filter_count} tone="muted" />
              <Stat label="In Mongo"          value={result.skipped_existing_mongo_count} tone="warn" />
              <Stat label="Mapping ignored"   value={result.skipped_mapping_ignored_count} tone="muted" />
              <Stat label="Limited by"        value={result.limited_by || "—"} tone="muted" />
            </div>

            <div className="mt-4 flex gap-3">
              <Button
                tier="secondary"
                onClick={() => { clearFile(); }}
                data-testid="csv-restart-button"
              >
                <RefreshCw size={14} /> Start over
              </Button>
              <Button
                tier="secondary"
                onClick={() => { window.location.href = "/admin/products"; }}
                data-testid="csv-goto-products-button"
              >
                View products <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
